import { v4 as uuidv4 } from 'uuid'
import {
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { TableIcon } from '@radix-ui/react-icons'
import { ToolbarSplitButton, DropdownOption } from './ToolbarSplitButton'
import { useDocumentState } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { useStepBOM } from '../OperationDocument/hooks/useBOM'
import {
  TableIdCellPlaceholder,
  TablePlaceholder,
} from './DraggablePlaceholders/TablePlaceholder'

type TableMenuProps = {
  isDisabled: boolean
}

export const TableMenu = ({ isDisabled }: TableMenuProps) => {
  const selectedPageId = useDocumentState((state) => state.currentPageInView)
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )

  const { data: cadData } = useCADQuery()
  const documentPage = cadData?.documentPages.find(
    (page) => page.id === selectedPageId,
  )

  const partsInOperation = useStepBOM({
    documentPageId: documentPage?.id,
  })

  const handleTable = async (e: React.MouseEvent) => {
    const firstAccessor = `custom-column--${uuidv4()}`
    const secondAccessor = `custom-column--${uuidv4()}`
    const tableData = Array.from(Array(2)).map(() => ({
      [firstAccessor]: '',
      [secondAccessor]: '',
    }))

    const tableColumns = [
      {
        [firstAccessor]: '',
      },
      {
        [secondAccessor]: '',
      },
    ]

    startAddingElement({
      newElementData: {
        Component: (props: any) => (
          <TablePlaceholder
            rows={tableData}
            columns={{
              [firstAccessor]: {
                title: '',
                width: 100,
              },
              [secondAccessor]: {
                title: '',
                width: 100,
              },
            }}
            {...props}
          />
        ),
        dataType: 'table',
        attributes: {
          columns: tableColumns,
          rows: tableData,
          type: 'generic-table',
        },
        documentPageId: documentPage?.id as string,
      },
      triggerLocation: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  const handlePartsTable = async (e: React.MouseEvent) => {
    const rows = partsInOperation.length
      ? partsInOperation.map((part, i) => {
          return {
            id: (
              <TableIdCellPlaceholder>
                {String.fromCharCode(65 + i)}
              </TableIdCellPlaceholder>
            ),
            partName: part.partName,
            quantity: part.quantity,
          }
        })
      : [
          {
            id: (
              <TableIdCellPlaceholder>
                {String.fromCharCode(65)}
              </TableIdCellPlaceholder>
            ),
            partName: '',
            quantity: '',
          },
        ]
    const columns = {
      id: {
        title: 'Id',
        width: 35,
      },
      partName: {
        title: 'Part Name',
        width: 340,
      },
      quantity: {
        title: 'Qty',
        width: 60,
      },
    }
    startAddingElement({
      newElementData: {
        Component: (props: any) => (
          <TablePlaceholder rows={rows} columns={columns} {...props} />
        ),
        dataType: 'table',
        attributes: {
          columns: [
            {
              id: 'Id',
            },
            {
              partName: 'Part Name',
            },
            {
              quantity: 'Qty',
            },
          ],
          rows: [],
          type: 'parts-table',
          isManual: true,
        },
        documentPageId: documentPage?.id as string,
      },
      triggerLocation: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  const handleToolsTable = async (e: React.MouseEvent) => {
    startAddingElement({
      newElementData: {
        Component: (props: any) => (
          <TablePlaceholder
            rows={[
              {
                toolName: '',
              },
            ]}
            columns={{
              toolName: {
                title: 'Tools',
                width: 200,
              },
            }}
            {...props}
          />
        ),
        dataType: 'table',
        attributes: {
          columns: [
            {
              toolName: 'Tools',
            },
          ],
          rows: [],
          type: 'tools-table',
          isManual: true,
        },
        documentPageId: documentPage?.id as string,
      },
      triggerLocation: {
        x: e.clientX,
        y: e.clientY,
      },
    })
  }

  const defaultOption: DropdownOption = {
    title: 'Table',
    description: 'Generic tabular data',
    Icon: TableIcon,
    handler: handleTable,
  }

  const dropdownOptions: DropdownOption[] = [
    {
      title: 'Parts Table',
      description: 'Additional parts table',
      Icon: Cog6ToothIcon,
      handler: handlePartsTable,
    },
    {
      title: 'Tools Table',
      description: 'Additional tools table',
      Icon: WrenchScrewdriverIcon,
      handler: handleToolsTable,
    },
  ]

  return (
    <ToolbarSplitButton
      Icon={TableIcon}
      defaultAction={handleTable}
      defaultOption={defaultOption}
      dropdownOptions={dropdownOptions}
      tooltip="Generic tabular data"
      isDisabled={isDisabled}
    />
  )
}
